import { type ComponentType } from "react";
import {
  Car,
  Bike,
  Home as HomeIcon,
  Tractor,
  Truck,
  Sparkles,
} from "lucide-react";
import { CTAButton, Section, SectionHeader, Reveal } from "@/components/ui-sr";
import { IconArrowRight } from "@/components/ui-sr/icons";
import { VERTICAIS_LIST, type VerticalKey } from "@/data/verticais";
import { SR_EASE_OUT } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { whatsappHref } from "@/site.config";

const ICON_MAP: Record<
  VerticalKey,
  ComponentType<{ className?: string }>
> = {
  veiculos: Car,
  motos: Bike,
  imoveis: HomeIcon,
  agro: Tractor,
  pesados: Truck,
  servicos: Sparkles,
};

const DESKTOP_STACK_CARD_FRAME =
  "h-[calc(100svh-4rem)] max-h-[50rem] min-h-[40rem]";
const DESKTOP_STACK_TOP_BASE = 20;
const DESKTOP_STACK_TOP_STEP = 20;

type VerticalItem = (typeof VERTICAIS_LIST)[number];
type IconType = ComponentType<{ className?: string }>;

type VerticalCardProps = {
  v: VerticalItem;
  Icon: IconType;
  stackDesktop?: boolean;
};

function VerticalCard({
  v,
  Icon,
  stackDesktop = false,
}: VerticalCardProps) {
  return (
    <article
      className={cn(
        "group relative w-full overflow-hidden bg-zinc-300 shadow-[var(--shadow-elevated)]",
        stackDesktop
          ? "h-full rounded-[28px] lg:rounded-[32px]"
          : "min-h-[26rem] rounded-[24px]"
      )}
    >
      {v.hero.bgImage && (
        <img
          src={v.hero.bgImage}
          alt={`Imagem de apoio do consórcio de ${v.navLabel}`}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/12" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(244,196,48,0.2),transparent_38%)]" />

      <div
        className={cn(
          "relative z-10 flex flex-col items-center justify-end text-center",
          stackDesktop
            ? "h-full px-5 pb-12 pt-20 sm:px-8 lg:px-12 lg:pb-14"
            : "min-h-[26rem] px-5 pb-8 pt-10 sm:px-8 lg:px-10"
        )}
      >
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-white/15 bg-black/35 text-[var(--sr-gold-light)] backdrop-blur-md">
          <Icon className="h-6 w-6" />
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--sr-gold-light)]/82">
          {v.hero.eyebrow}
        </p>

        <h3 className="mt-3 max-w-3xl text-balance text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-5xl">
          {v.hero.title}
          <span className="block sr-text-gradient-gold">
            {v.hero.highlight}
          </span>
        </h3>

        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/78 sm:text-base lg:text-lg">
          {v.hero.subtitle}
        </p>

        <div className="mt-7 flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:items-center">
          <CTAButton
            as="a"
            href={whatsappHref(v.ctaWhatsAppMessage)}
            target="_blank"
            rel="noopener noreferrer"
            variant="red"
            size="lg"
            className="sm:min-w-[250px]"
            trackName="whatsapp_clicked"
            trackParams={{ source: `vertical_card_${v.key}` }}
          >
            Simular consórcio de {v.navLabel}
          </CTAButton>
          <CTAButton
            as="a"
            href={v.routePath}
            variant="light-outline"
            size="lg"
            className="sm:min-w-[180px]"
            iconRight={<IconArrowRight className="h-4 w-4" />}
          >
            Saiba mais
          </CTAButton>
        </div>
      </div>
    </article>
  );
}

type DesktopStickyCardProps = {
  v: VerticalItem;
  Icon: IconType;
  index: number;
};

function DesktopStickyCard({
  v,
  Icon,
  index,
}: DesktopStickyCardProps) {
  const top = DESKTOP_STACK_TOP_BASE + index * DESKTOP_STACK_TOP_STEP;

  return (
    <div
      data-stack-card={index}
      className={cn("sticky w-full origin-top", DESKTOP_STACK_CARD_FRAME)}
      style={{ top, zIndex: index + 1 }}
    >
      <VerticalCard v={v} Icon={Icon} stackDesktop />
    </div>
  );
}

function DesktopVerticalStack() {
  return (
    <div className="mt-14 hidden lg:block" data-verticais-stack>
      <div className="mx-auto max-w-[58rem]">
        {VERTICAIS_LIST.map((v, index) => {
          const Icon = ICON_MAP[v.key];

          return (
            <DesktopStickyCard
              key={v.key}
              v={v}
              Icon={Icon}
              index={index}
            />
          );
        })}
      </div>
    </div>
  );
}

export function SixVerticalsGrid() {
  return (
    <Section tone="dark" id="verticais">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(244,196,48,0.15),transparent_30%)]" />
      <SectionHeader
        eyebrow="6 consórcios"
        title={
          <>
            Os tipos de carta, no ritmo certo para
            <span className="block sr-text-gradient-gold">
              começar pelo que faz sentido agora.
            </span>
          </>
        }
        description="Compare as cartas, veja os detalhes e fale com a equipe Bazilio quando fizer sentido."
        align="center"
        tone="light"
        divider
      />

      <DesktopVerticalStack />

      <div className="mx-auto mt-12 max-w-5xl space-y-6 lg:hidden">
        {VERTICAIS_LIST.map((v, i) => {
          const Icon = ICON_MAP[v.key];

          return (
            <Reveal
              key={v.key}
              variants={{
                hidden: { opacity: 0, y: 24 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { delay: i * 0.08, ease: SR_EASE_OUT },
                },
              }}
            >
              <VerticalCard v={v} Icon={Icon} />
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
}
